/* eslint-disable max-lines */
import * as c from './types';
import createReducer from '../../reducers/utils/createReducer';
import { BulkCreateError } from 'types';

enum Status {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error'
}

export interface UsersList {
  status: Status;
  data: any[];
  operators: any[];
  operatorsIsLoading: boolean;
  operatorsUpdateLoading: boolean;
  failedOperatorEntries: BulkCreateError[];
  error?: any;
  inviteStatus: Status;
  inviteError?: any;
  deleteStatus: Status;
  deleteError?: any;
  updateStatus: Status;
  updateError?: any;
  createStatus: Status;
  createError?: any;
  updateOperatorStatus: Status;
}

const initialState: UsersList = {
  status: Status.IDLE,
  data: [],
  operators: [],
  operatorsIsLoading: false,
  operatorsUpdateLoading: false,
  error: undefined,
  failedOperatorEntries: [],
  inviteStatus: Status.IDLE,
  inviteError: undefined,
  deleteStatus: Status.IDLE,
  deleteError: undefined,
  updateStatus: Status.IDLE,
  updateError: undefined,
  createStatus: Status.IDLE,
  createError: undefined,
  updateOperatorStatus: Status.IDLE
};

export default createReducer(initialState, {
  [c.FETCH_USERS_LIST_REQUEST]: (state = initialState) => ({
    ...state,
    status: Status.LOADING,
    error: null
  }),
  [c.FETCH_USERS_LIST_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: any[] }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      data: action.payload?.filter((e) => e.role !== 'operator'),
      status: Status.SUCCESS
    };
  },
  [c.FETCH_USERS_LIST_ERROR]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      error: action.payload,
      status: Status.ERROR
    };
  },
  [c.FETCH_OPERATORS_LIST_REQUEST]: (state = initialState) => ({
    ...state,
    operatorsIsLoading: true,
    error: null
  }),
  [c.FETCH_OPERATORS_LIST_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: any[] }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      operators: action.payload,
      operatorsIsLoading: false
    };
  },
  [c.FETCH_OPERATORS_LIST_ERROR]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      error: action.payload,
      operatorsIsLoading: false
    };
  },
  [c.INVITE_USER_REQUEST]: (state = initialState) => ({
    ...state,
    inviteStatus: Status.LOADING,
    inviteError: null
  }),
  [c.INVITE_USER_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      data: [...[action.payload.data], ...state.data],
      inviteStatus: Status.SUCCESS
    };
  },
  [c.INVITE_USER_ERROR]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      inviteError: action.payload,
      inviteStatus: Status.ERROR
    };
  },
  [c.DELETE_USER_REQUEST]: (state = initialState) => ({
    ...state,
    deleteStatus: Status.LOADING,
    deleteError: null
  }),
  [c.DELETE_USER_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: { data: { email: string } } }
  ) => {
    if (!action.payload) return state;
    const data = state.data.filter(
      (user) => user.email !== action.payload?.data.email
    );
    return {
      ...state,
      data: data,
      deleteStatus: Status.SUCCESS
    };
  },
  [c.DELETE_USER_ERROR]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      deleteError: action.payload,
      deleteStatus: Status.ERROR
    };
  },
  [c.UPDATE_USER_REQUEST]: (state = initialState) => ({
    ...state,
    updateStatus: Status.LOADING,
    updateError: null
  }),
  [c.UPDATE_USER_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    const users = [...state.data];
    const user = users.find((user) => user.email === action.payload.email);
    if (!user) return state;
    user.role = action.payload.role;
    user.companies = action.payload.companies;
    return {
      ...state,
      data: users,
      updateStatus: Status.SUCCESS
    };
  },
  [c.UPDATE_USER_ERROR]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      updateError: action.payload,
      updateStatus: Status.ERROR
    };
  },
  [c.UPDATE_OPERATOR_REQUEST]: (state = initialState) => ({
    ...state,
    operatorsUpdateLoading: true,
    updateOperatorStatus: Status.LOADING
  }),
  [c.UPDATE_OPERATOR_SUCCESS]: (state = initialState) => ({
    ...state,
    operatorsUpdateLoading: false,
    updateOperatorStatus: Status.SUCCESS
  }),
  [c.UPDATE_OPERATOR_ERROR]: (state = initialState) => ({
    ...state,
    operatorsUpdateLoading: false,
    updateOperatorStatus: Status.ERROR
  }),
  [c.FILTER_USER]: (
    state = initialState,
    action: { type: string; payload?: string }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      data: state.data.filter((user) => user.email.includes(action.payload))
    };
  },
  [c.CREATE_USER_REQUEST]: (state = initialState) => ({
    ...state,
    createStatus: Status.LOADING,
    createError: null,
    failedOperatorEntries: []
  }),
  [c.CREATE_USER_SUCCESS]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      data: [...[action.payload], ...state.data],
      createStatus: Status.SUCCESS,
      failedOperatorEntries: []
    };
  },
  [c.CREATE_USER_ERROR]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      createError: action.payload,
      createStatus: Status.ERROR,
      failedOperatorEntries: action.payload ?? []
    };
  },
  [c.BULK_DELETE_OPERATORS_REQUEST]: (state = initialState) => ({
    ...state,
    deleteStatus: Status.LOADING,
    deleteError: null
  }),
  [c.BULK_DELETE_OPERATORS_SUCCESS]: (state = initialState) => {
    return {
      ...state,
      deleteError: null,
      deleteStatus: Status.SUCCESS
    };
  },
  [c.BULK_DELETE_OPERATORS_ERROR]: (
    state = initialState,
    action: { type: string; payload?: any }
  ) => {
    if (!action.payload) return state;
    return {
      ...state,
      deleteError: action.payload,
      deleteStatus: Status.ERROR
    };
  }
});
